import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faPlay, 
  faPause, 
  faScissors, 
  faArrowUp,
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import styles from './AudioTrimmerModal.module.css';

const AudioTrimmerModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  file, 
  maxDuration, 
  originalDuration 
}) => {
  const navigate = useNavigate();
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const audioUrl = useRef(null);
  const isMountedRef = useRef(true);
  const cleanupTimeoutRef = useRef(null);
  
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duration, setDuration] = useState(originalDuration || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Format seconds to MM:SS
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Safe cleanup function with timeout
  const cleanup = useCallback(() => {
    // Clear any pending cleanup
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    
    if (wavesurfer.current) {
      try {
        // Stop playback first
        if (typeof wavesurfer.current.stop === 'function') {
          wavesurfer.current.stop();
        }
        
        // Use a timeout to allow any pending operations to complete
        cleanupTimeoutRef.current = setTimeout(() => {
          if (wavesurfer.current && typeof wavesurfer.current.destroy === 'function') {
            try {
              wavesurfer.current.destroy();
            } catch (error) {
              console.warn('WaveSurfer destroy error (safe to ignore):', error.message);
            }
          }
          wavesurfer.current = null;
        }, 100);
        
      } catch (error) {
        console.warn('WaveSurfer cleanup error (safe to ignore):', error.message);
        wavesurfer.current = null;
      }
    }
    
    if (audioUrl.current) {
      try {
        URL.revokeObjectURL(audioUrl.current);
      } catch (error) {
        console.warn('URL cleanup error:', error);
      }
      audioUrl.current = null;
    }
  }, []);

  // Initialize WaveSurfer with better error handling
  useEffect(() => {
    isMountedRef.current = true;
    
    if (!isOpen || !file || !waveformRef.current) return;

    // Cleanup any existing instance
    cleanup();

    // Reset states
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setIsDragging(false);

    // Create audio URL
    audioUrl.current = URL.createObjectURL(file);

    // Delay WaveSurfer creation to avoid conflicts
    const initTimeout = setTimeout(() => {
      if (!isMountedRef.current || !waveformRef.current) return;
      
      try {
        // Create new WaveSurfer instance
        wavesurfer.current = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: '#E2384D',
          progressColor: '#B91C3C',
          cursorColor: '#ffffff',
          barWidth: 2,
          barRadius: 1,
          height: 120,
          normalize: true,
          responsive: true,
          interact: true,
          hideScrollbar: true,
          backend: 'WebAudio',
          mediaControls: false
        });

        // Set up event listeners with safety checks
        const handleReady = () => {
          if (!isMountedRef.current || !wavesurfer.current) return;
          
          try {
            const audioDuration = wavesurfer.current.getDuration();
            setDuration(audioDuration);
            setIsReady(true);
            
            // Set initial trim values
            setTrimStart(0);
            setTrimEnd(Math.min(audioDuration, maxDuration));
          } catch (error) {
            console.error('Error in ready handler:', error);
          }
        };

        const handleAudioProcess = (time) => {
          if (!isMountedRef.current || isDragging) return;
          
          setCurrentTime(time);
          
          // Auto-pause at trim end
          if (time >= trimEnd && isPlaying && wavesurfer.current) {
            try {
              wavesurfer.current.pause();
            } catch (error) {
              console.warn('Error pausing:', error);
            }
          }
        };

        const handleSeek = (progress) => {
          if (!isMountedRef.current || isDragging) return;
          setCurrentTime(progress * duration);
        };

        const handlePlay = () => isMountedRef.current && setIsPlaying(true);
        const handlePause = () => isMountedRef.current && setIsPlaying(false);
        const handleFinish = () => isMountedRef.current && setIsPlaying(false);

        // Add event listeners
        wavesurfer.current.on('ready', handleReady);
        wavesurfer.current.on('audioprocess', handleAudioProcess);
        wavesurfer.current.on('seek', handleSeek);
        wavesurfer.current.on('play', handlePlay);
        wavesurfer.current.on('pause', handlePause);
        wavesurfer.current.on('finish', handleFinish);

        // Load audio
        if (audioUrl.current && isMountedRef.current) {
          wavesurfer.current.load(audioUrl.current);
        }

      } catch (error) {
        console.error('Error initializing WaveSurfer:', error);
        setIsReady(false);
      }
    }, 100);

    // Cleanup on unmount or dependency change
    return () => {
      isMountedRef.current = false;
      clearTimeout(initTimeout);
      cleanup();
    };
  }, [isOpen, file, maxDuration, cleanup, isDragging, trimEnd, isPlaying, duration]);

  // Control functions with safety checks
  const togglePlayPause = useCallback(() => {
    if (!wavesurfer.current || !isReady || !isMountedRef.current) return;
    
    try {
      if (isPlaying) {
        wavesurfer.current.pause();
      } else {
        // Seek to trim start if outside range
        if (currentTime < trimStart || currentTime >= trimEnd) {
          const position = trimStart / duration;
          wavesurfer.current.seekTo(position);
        }
        wavesurfer.current.play();
      }
    } catch (error) {
      console.error('Error toggling playback:', error);
    }
  }, [isReady, isPlaying, currentTime, trimStart, trimEnd, duration]);

  const playTrimmedSection = useCallback(() => {
    if (!wavesurfer.current || !isReady || !isMountedRef.current) return;
    
    try {
      const position = trimStart / duration;
      wavesurfer.current.seekTo(position);
      wavesurfer.current.play();
    } catch (error) {
      console.error('Error playing trimmed section:', error);
    }
  }, [isReady, trimStart, duration]);

  const stopPlayback = useCallback(() => {
    if (!wavesurfer.current || !isMountedRef.current) return;
    
    try {
      wavesurfer.current.stop();
      setIsPlaying(false);
      setCurrentTime(0);
    } catch (error) {
      console.error('Error stopping playback:', error);
    }
  }, []);

  // Slider handlers
  const handleTrimStartChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    const newStart = Math.max(0, Math.min(value, trimEnd - 1));
    setTrimStart(newStart);
    
    if (wavesurfer.current && isReady && isMountedRef.current) {
      try {
        const position = newStart / duration;
        wavesurfer.current.seekTo(position);
      } catch (error) {
        console.error('Error seeking:', error);
      }
    }
  }, [trimEnd, duration, isReady]);

  const handleTrimEndChange = useCallback((e) => {
    const value = parseFloat(e.target.value);
    const newEnd = Math.min(duration, Math.max(value, trimStart + 1));
    const maxEnd = trimStart + maxDuration;
    setTrimEnd(Math.min(newEnd, maxEnd));
  }, [duration, trimStart, maxDuration]);

  // Waveform click handler
  const handleWaveformClick = useCallback((e) => {
    if (!waveformRef.current || !isReady || isDragging || !isMountedRef.current) return;
    
    const rect = waveformRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const newTime = percent * duration;
    
    // Constrain to trim range
    const constrainedTime = Math.max(trimStart, Math.min(newTime, trimEnd));
    
    if (wavesurfer.current) {
      try {
        const position = constrainedTime / duration;
        wavesurfer.current.seekTo(position);
      } catch (error) {
        console.error('Error seeking on click:', error);
      }
    }
  }, [isReady, isDragging, duration, trimStart, trimEnd]);

  // Create trimmed file
  const createTrimmedFile = useCallback(async () => {
    try {
      const trimmedFile = new File([file], file.name, {
        type: file.type,
        lastModified: file.lastModified
      });
      
      trimmedFile.trimStart = trimStart;
      trimmedFile.trimEnd = trimEnd;
      trimmedFile.originalDuration = duration;
      trimmedFile.isTrimmed = true;
      
      return trimmedFile;
    } catch (error) {
      console.error('Error creating trimmed file:', error);
      throw error;
    }
  }, [file, trimStart, trimEnd, duration]);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsProcessing(true);
    try {
      const trimmedFile = await createTrimmedFile();
      await onSave(trimStart, trimEnd, trimmedFile);
      onClose();
    } catch (error) {
      console.error('Error saving trimmed audio:', error);
      alert('Error processing audio. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [createTrimmedFile, onSave, trimStart, trimEnd, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    isMountedRef.current = false;
    stopPlayback();
    
    // Delay cleanup to prevent conflicts
    setTimeout(() => {
      cleanup();
      onClose();
    }, 50);
  }, [stopPlayback, cleanup, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const selectedDuration = trimEnd - trimStart;
  const isValidSelection = selectedDuration <= maxDuration && selectedDuration >= 1;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <h2>
          <FontAwesomeIcon icon={faScissors} /> Trim Your Audio
        </h2>
        
        <div className={styles['trim-info']}>
          <p>Your audio is {formatTime(duration)} long, but your membership allows up to {formatTime(maxDuration)}.</p>
          <p>Use the sliders below to select which part of the audio to keep, or click on the waveform to navigate.</p>
        </div>
        
        {/* Waveform Container */}
        <div className={styles['waveform-container']}>
          <div 
            ref={waveformRef} 
            className={styles['waveform']}
            onClick={handleWaveformClick}
          />
          {!isReady && (
            <div className={styles['loading-waveform']}>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading audio...</span>
            </div>
          )}
          
          {/* Trim overlay indicators */}
          {isReady && duration > 0 && (
            <>
              <div 
                className={styles['trim-start-line']}
                style={{ left: `${(trimStart / duration) * 100}%` }}
              />
              <div 
                className={styles['trim-end-line']}
                style={{ left: `${(trimEnd / duration) * 100}%` }}
              />
              <div 
                className={styles['trim-selection-overlay']}
                style={{
                  left: `${(trimStart / duration) * 100}%`,
                  width: `${((trimEnd - trimStart) / duration) * 100}%`
                }}
              />
            </>
          )}
        </div>
        
        {isReady && (
          <>
            {/* Trim Controls */}
            <div className={styles['trim-controls']}>
              <div className={styles['slider-group']}>
                <label>Start: {formatTime(trimStart)}</label>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  step="0.1"
                  value={trimStart}
                  onChange={handleTrimStartChange}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={() => setIsDragging(false)}
                  onTouchStart={() => setIsDragging(true)}
                  onTouchEnd={() => setIsDragging(false)}
                  className={styles['trim-slider']}
                />
              </div>
              
              <div className={styles['slider-group']}>
                <label>End: {formatTime(trimEnd)}</label>
                <input
                  type="range"
                  min="0"
                  max={duration}
                  step="0.1"
                  value={trimEnd}
                  onChange={handleTrimEndChange}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={() => setIsDragging(false)}
                  onTouchStart={() => setIsDragging(true)}
                  onTouchEnd={() => setIsDragging(false)}
                  className={styles['trim-slider']}
                />
              </div>
            </div>
            
            {/* Playback Controls */}
            <div className={styles['playback-controls']}>
              <button 
                onClick={togglePlayPause}
                className={styles['control-button']}
                disabled={!isReady}
              >
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              
              <button 
                onClick={playTrimmedSection}
                className={styles['control-button']}
                disabled={!isReady}
              >
                <FontAwesomeIcon icon={faPlay} />
                Preview Selection
              </button>
              
              <button 
                onClick={stopPlayback}
                className={styles['control-button']}
                disabled={!isReady}
              >
                Stop
              </button>
            </div>
            
            {/* Selection Info */}
            <div className={styles['selection-info']}>
              <div className={styles['time-display']}>
                <span>Selection: {formatTime(trimStart)} - {formatTime(trimEnd)}</span>
                <span className={selectedDuration > maxDuration ? styles['invalid'] : styles['valid']}>
                  Duration: {formatTime(selectedDuration)} / {formatTime(maxDuration)}
                </span>
              </div>
              
              {selectedDuration > maxDuration && (
                <div className={styles['warning']}>
                  Selection exceeds maximum duration. Please adjust the selection.
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className={styles['form-actions']}>
              <button 
                type="button" 
                className={styles['secondary-button']}
                onClick={() => navigate('/membership')}
              >
                <FontAwesomeIcon icon={faArrowUp} /> Upgrade Membership
              </button>
              <button 
                type="button" 
                className={styles['primary-button']}
                onClick={handleSave}
                disabled={!isValidSelection || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                  </>
                ) : (
                  'Apply Trim'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AudioTrimmerModal;